export function extractDateTimeFromPict(pict) {
  if (!pict) return null;
  if (pict) {
    const regex = /_(\d{4}-\d{2}-\d{2})_(\d{6})\.jpg$/;
    const match = pict.match(regex);
    if (match) {
      const date = match[1];
      const time = match[2];
      const dateTimeString = `${date}T${time.substring(0, 2)}:${time.substring(
        2,
        4,
      )}:${time.substring(4, 6)}Z`;
      const dateTime = new Date(dateTimeString);

      return dateTime.toLocaleString();
    }
  }
  return null;
}

export function displayUserTimeZone() {
  const date = new Date();
  // Get the short time zone abbreviation, e.g., 'EST', 'PST', etc.
  // This works in most modern browsers
  const parts = date.toLocaleTimeString('en-US', { timeZoneName: 'short' }).split(' ');
  return (
    parts[2] ||
    Intl.DateTimeFormat('en-US', { timeZoneName: 'short' })
      .formatToParts(date)
      .find((p) => p.type === 'timeZoneName')?.value ||
    ''
  );
}

export function getDateOrdinal(n) {
  const s = ['th', 'st', 'nd', 'rd'],
    v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

/**
 * Parses a date/time value and returns a Date object when parsing succeeds and null when parsing fails.
 * Supports valid Date objects, "MM/DD/YYYY HH:MM UTC", "MM/DD/YYYY HH:MM:SS UTC", and all other formats supported by the JavaScript Date constructor.
 * Handles null, undefined, empty strings, whitespace-only strings, and invalid dates by returning null.
 */
export const parseDateTime = (dateTime) => {
  if (!dateTime) return null;

  if (dateTime instanceof Date) return Number.isNaN(dateTime.getTime()) ? null : dateTime; // Determines if its a valid Date object

  const safeDateTime = String(dateTime).trim(); // Convert to string and trim whitespace
  if (!safeDateTime) return null; // Check if the string is empty after trimming (e.g. null, undefined, empty string)

  const utcDateTime = safeDateTime.match(
    /^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})(?::(\d{2}))?\s*UTC$/i,
  ); // Matches "MM/DD/YYYY HH:MM[:SS] UTC" format, case-insensitive

  if (utcDateTime) {
    const [, month, day, year, hour, minute, second = '0'] = utcDateTime; // extracts the matches from the regex (month, day, year, hour, minute, second)

    const parsedDate = new Date(
      Date.UTC(
        Number(year),
        Number(month) - 1,
        Number(day),
        Number(hour),
        Number(minute),
        Number(second),
      ),
    ); // Create a Date object in UTC (month is 0-indexed)

    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate; // Check if the date is valid
  }

  const parsedDate = new Date(safeDateTime); // Fallback to Date constructor for other formats
  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

/**
 * Returns the later of two parseable date/time values.
 * Returns the valid value when only one can be parsed, or null when neither can be parsed.
 */
export const getLaterDateTime = (firstValue, secondValue) => {
  const firstDate = parseDateTime(firstValue);
  const secondDate = parseDateTime(secondValue);

  if (!firstDate) return secondDate;
  if (!secondDate) return firstDate;

  return firstDate > secondDate ? firstDate : secondDate;
};

/**
 * This will add zeros to the start of a string if it is less than two characters long up until the string is at least two characters.
 */
const padDateTimePart = (value) => String(value).padStart(2, '0');

/**
 * Formats a date/time as an ISO-like local timestamp with its numeric UTC offset.
 * Returns an empty string when the input cannot be parsed.
 */
export const formatDateTimeWithOffset = (dateTime = new Date()) => {
  const date = parseDateTime(dateTime);
  if (!date) return '';

  const offsetMinutes = -date.getTimezoneOffset(); // Flips the offset sign so it provides how many minutes local time is from UTC.
  const offsetSign = offsetMinutes >= 0 ? '+' : '-'; // Positive if local time is ahead of UTC, negative if behind.
  const absoluteOffsetMinutes = Math.abs(offsetMinutes); // Stores the offeset minutes without the negative sign if it had one.

  return `${date.getFullYear()}-${padDateTimePart(date.getMonth() + 1)}-${padDateTimePart(
    date.getDate(),
  )}T${padDateTimePart(date.getHours())}:${padDateTimePart(
    date.getMinutes(),
  )}:${padDateTimePart(date.getSeconds())}${offsetSign}${padDateTimePart(
    Math.floor(absoluteOffsetMinutes / 60), // Gets the hours part of the offset and truncates off the decimal part.
  )}:${padDateTimePart(absoluteOffsetMinutes % 60)}`; // Gets the minutes part of the offset by checking the remainder.
};

/**
 * Formats a date/time for a local `datetime-local` HTML input.
 * Returns an empty string when the input cannot be parsed.
 */
export const formatDateTimeLocalInput = (dateTime) => {
  const date = parseDateTime(dateTime);
  if (!date) return '';

  return `${date.getFullYear()}-${padDateTimePart(date.getMonth() + 1)}-${padDateTimePart(
    date.getDate(),
  )}T${padDateTimePart(date.getHours())}:${padDateTimePart(date.getMinutes())}`;
};

/**
 * Returns the user's IANA time-zone name and the numeric UTC offset for a date/time.
 */
export const formatTimeZoneWithOffset = (dateTime = new Date()) => {
  const date = parseDateTime(dateTime) ?? new Date();
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Local time'; // Gets IANA timezone names like "America/Chicago"
  const offsetMinutes = -date.getTimezoneOffset(); // Flips the offset sign so it provides how many minutes local time is from UTC.
  const offsetSign = offsetMinutes >= 0 ? '+' : '-'; // Positive if local time is ahead of UTC, negative if behind.
  const absoluteOffsetMinutes = Math.abs(offsetMinutes); // Stores the offset minutes without the negative sign if it had one.

  return `${timeZone} (UTC${offsetSign}${padDateTimePart(
    Math.floor(absoluteOffsetMinutes / 60),
  )}:${padDateTimePart(absoluteOffsetMinutes % 60)})`;
};

/**
 * Parses a date/time value with parseDateTime, then formats it into a localized date and time string.
 * Formats the result using the user's locale with month, day, year, hour, minute, and short time zone name.
 * Allows the month format to be changed with monthType, such as "short" or "long".
 * Returns the fallback value when parseDateTime returns null.
 */
export const formatDateTime = (
  dateTime,
  monthType = 'short',
  fallback = 'N/A',
  hideTime = false,
) => {
  const parsedDate = parseDateTime(dateTime);

  if (!parsedDate) return fallback;

  // Formats the date using the user's locale (undefined means use the user's locale such as "en-US")
  return new Intl.DateTimeFormat(undefined, {
    month: monthType, // Use abbreviated month name by default (e.g. "Mar")
    day: '2-digit', // Use 2-digit day (e.g. "05")
    year: 'numeric', // Use numeric year (e.g. "2024")
    hour: hideTime ? undefined : 'numeric', // Use numeric hour (e.g. "3 PM")
    minute: hideTime ? undefined : '2-digit', // Use 2-digit minute (e.g. "05")
    timeZoneName: hideTime ? undefined : 'short', // Use short time zone name (e.g. "PST")
  }).format(parsedDate);
};

/**
 * Formats a start and end date/time value into a localized date/time range string.
 * Uses formatDateTime for both values with the provided monthType and returns fallback when both values are invalid.
 * Returns "Until {end}" when only the end time is valid and "Starts {start}" when only the start time is valid.
 * Returns "{start} - {end}" when both start and end times are valid.
 */
export const formatDateTimeRange = (startTime, endTime, monthType = 'short', fallback = 'N/A') => {
  const formattedStart = formatDateTime(startTime, monthType, null);
  const formattedEnd = formatDateTime(endTime, monthType, null);

  if (!formattedStart && !formattedEnd) return fallback;
  if (!formattedStart) return `Until ${formattedEnd}`;
  if (!formattedEnd) return `Starts ${formattedStart}`;

  return `${formattedStart} - ${formattedEnd}`;
};

/**
 * Converts an epoch timestamp (ms) to a datetime-local string (YYYY-MM-DDTHH:MM) in UTC.
 * Returns an empty string when the value is null/undefined/0.
 */
export function epochToDatetimeLocal(epochMs) {
  if (!epochMs) return '';
  return new Date(epochMs).toISOString().slice(0, 16);
}

/**
 * Converts a datetime-local string (YYYY-MM-DDTHH:MM) to an epoch timestamp (ms).
 * Returns null for empty/invalid input.
 */
export function datetimeLocalToEpoch(dtStr) {
  if (!dtStr) return null;
  return new Date(dtStr).getTime();
}
